(() => {
  const SIZE_NAMES = new Set(['talla', 'tallas', 'size', 'sizes']);
  // Keep confirmation across Horizon section re-renders. A WeakMap tied to the
  // original DOM node loses its state when the variant picker is replaced.
  const scopeStates = new Map();

  const normalize = (value = '') =>
    value
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const getOptionName = (control) => {
    const selectedOptionName = control?.selectedOptions?.[0]?.dataset?.optionName;
    const rawName = control?.dataset?.optionName || selectedOptionName || control?.name || '';
    return rawName.replace(/^options\[/, '').replace(/\]$/, '');
  };

  const isSizeControl = (control) => SIZE_NAMES.has(normalize(getOptionName(control)));

  const getScope = (element) =>
    element.closest('#quick-add-modal-content') ||
    element.closest('[id^="shopify-section-"]') ||
    element.closest('main') ||
    document.body;

  const getScopeKey = (scope, productId) => {
    const scopeId =
      scope.id || scope.dataset?.sectionId || scope.getAttribute?.('data-section-id') || scope.tagName || 'page';
    return `${scopeId}:${productId}`;
  };

  const getState = (scope, productId) => {
    const key = getScopeKey(scope, productId);
    if (!scopeStates.has(key)) scopeStates.set(key, { confirmed: false });
    return scopeStates.get(key);
  };

  const getExistingState = (scope, productId) => scopeStates.get(getScopeKey(scope, productId));

  const getProductButtons = (scope, productId) => {
    const selector = [
      `product-form-component[data-product-id="${productId}"] button[type="submit"][name="add"]`,
      `sticky-add-to-cart[data-product-id="${productId}"] .sticky-add-to-cart__button`,
    ].join(',');

    return [...scope.querySelectorAll(selector)].filter(
      (button) => !button.classList.contains('quick-add__button--choose')
    );
  };

  const getPaymentBlocks = (scope, productId) =>
    [...scope.querySelectorAll(`product-form-component[data-product-id="${productId}"] .accelerated-checkout-block`)];

  const findSizeGroup = (picker) => {
    const controls = [...picker.querySelectorAll('[data-option-name]')].filter(isSizeControl);
    if (!controls.length) return null;
    return controls[0].closest('fieldset, .variant-option--dropdowns, .variant-option') || null;
  };

  const gateButtons = (scope, productId) => {
    for (const button of getProductButtons(scope, productId)) {
      if (!button.dataset.trenderSizeGate) {
        button.dataset.trenderSizeWasDisabled = button.disabled ? 'true' : 'false';
      }
      button.dataset.trenderSizeGate = 'true';
      button.classList.add('trender-size-gated-button');
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    }

    for (const block of getPaymentBlocks(scope, productId)) {
      if (!block.dataset.trenderSizeGate) {
        block.dataset.trenderSizeWasHidden = block.hidden ? 'true' : 'false';
      }
      block.dataset.trenderSizeGate = 'true';
      block.hidden = true;
    }
  };

  const releaseButtons = (scope, productId) => {
    for (const button of getProductButtons(scope, productId)) {
      if (button.dataset.trenderSizeGate !== 'true') continue;
      if (button.dataset.trenderSizeWasDisabled !== 'true') button.disabled = false;
      button.classList.remove('trender-size-gated-button');
      button.removeAttribute('aria-disabled');
      delete button.dataset.trenderSizeGate;
      delete button.dataset.trenderSizeWasDisabled;
    }

    for (const block of getPaymentBlocks(scope, productId)) {
      if (block.dataset.trenderSizeGate !== 'true') continue;
      if (block.dataset.trenderSizeWasHidden !== 'true') block.hidden = false;
      delete block.dataset.trenderSizeGate;
      delete block.dataset.trenderSizeWasHidden;
    }
  };

  const initializePicker = (picker) => {
    const group = findSizeGroup(picker);
    if (!group) return;

    const productId = picker.dataset.productId;
    if (!productId) return;

    const scope = getScope(picker);
    const state = getState(scope, productId);
    picker.dataset.trenderSizeGateReady = 'true';

    if (state.confirmed) {
      picker.classList.remove('trender-size-unconfirmed');
      releaseButtons(scope, productId);
      return;
    }

    picker.classList.add('trender-size-unconfirmed');
    gateButtons(scope, productId);
  };

  const initialize = (root = document) => {
    const pickers = root.matches?.('variant-picker') ? [root] : root.querySelectorAll?.('variant-picker') || [];
    for (const picker of pickers) initializePicker(picker);
  };

  const confirmPicker = (picker) => {
    const productId = picker.dataset.productId;
    if (!productId) return;

    const scope = getScope(picker);
    const state = getState(scope, productId);
    state.confirmed = true;

    for (const relatedPicker of scope.querySelectorAll(`variant-picker[data-product-id="${productId}"]`)) {
      relatedPicker.classList.remove('trender-size-unconfirmed');
      relatedPicker.classList.add('trender-size-confirmed');
    }

    window.setTimeout(() => releaseButtons(scope, productId), 350);
  };

  const focusPrompt = (scope, productId) => {
    const picker = scope.querySelector(`variant-picker[data-product-id="${productId}"]`);
    const group = picker ? findSizeGroup(picker) : null;
    const heading = group?.querySelector('.trender-size-heading, legend, label');
    (heading || group)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    group?.querySelector('input, select')?.focus({ preventScroll: true });
  };

  document.addEventListener(
    'click',
    (event) => {
      const control = event.target.closest?.('input[data-option-name], label');
      let input =
        control?.matches?.('input[data-option-name]') ? control : control?.querySelector?.('input[data-option-name]');
      if (!input && control?.matches?.('label') && control.htmlFor) {
        input = document.getElementById(control.htmlFor);
      }
      const picker = input?.closest?.('variant-picker');
      if (picker && isSizeControl(input)) {
        if (input.getAttribute('aria-disabled') === 'true' || input.disabled) return;
        confirmPicker(picker);
        return;
      }

      const button = event.target.closest?.('.add-to-cart-button');
      if (!button || button.classList.contains('quick-add__button--choose')) return;
      const owner =
        button.closest('product-form-component[data-product-id]') ||
        button.closest('sticky-add-to-cart[data-product-id]');
      const productId = owner?.dataset?.productId;
      if (!productId) return;
      const scope = getScope(button);
      const state = getExistingState(scope, productId);
      if (state && !state.confirmed) {
        event.preventDefault();
        event.stopImmediatePropagation();
        focusPrompt(scope, productId);
      }
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const control = event.target;
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
      if (!isSizeControl(control)) return;
      const picker = control.closest('variant-picker');
      if (picker) confirmPicker(picker);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const formComponent = event.target.closest?.('product-form-component[data-product-id]');
      const productId = formComponent?.dataset?.productId;
      if (!productId) return;
      const scope = getScope(formComponent);
      const state = getExistingState(scope, productId);
      if (state && !state.confirmed) {
        event.preventDefault();
        event.stopImmediatePropagation();
        focusPrompt(scope, productId);
      }
    },
    true
  );

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) initialize(node);
      }
    }
  });

  const start = () => {
    initialize();
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
